// File: src/contexts/AssistantContext.jsx
// Note: No persistence here. Left untouched (folder-only storage not needed).

import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

const AssistantContext = createContext(null);

export function AssistantProvider({ children }) {
  const [suggestions, setSuggestions] = useState([]);
  const [confirmUI, setConfirmUI] = useState(null); // optional custom confirm modal

  const pushSuggestion = useCallback((s) => {
    const id = s.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setSuggestions((prev) => [{ ...s, id }, ...prev]);
    return id;
  }, []);

  const removeSuggestion = useCallback(
    (id) => setSuggestions((prev) => prev.filter((s) => s.id !== id)),
    []
  );

  const clearSuggestions = useCallback(() => setSuggestions([]), []);

  const confirmable = useCallback(
    async ({ title, details, onConfirm, onUndo }) => {
      if (!confirmUI) {
        if (!window.confirm(`${title}\n\n${details || ""}`)) return { ok: false };
        const result = await onConfirm?.();
        if (onUndo && window.confirm("Undo last action?")) await onUndo(result);
        return { ok: true, result };
      }
      return confirmUI({ title, details, onConfirm, onUndo });
    },
    [confirmUI]
  );

  const value = useMemo(
    () => ({
      suggestions,
      pushSuggestion,
      removeSuggestion,
      clearSuggestions,
      confirmable,
      setConfirmUI,
    }),
    [suggestions, pushSuggestion, removeSuggestion, clearSuggestions, confirmable]
  );

  return <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>;
}

export const useAssistant = () => useContext(AssistantContext);
